import {
  ActionReducer,
  createAction,
  createFeatureSelector,
  createReducer,
  createSelector,
  DefaultProjectorFn,
  on,
  props,
} from '@ngrx/store';
import { ActionCreator } from '@ngrx/store'; // TypedAction removido, não faz parte da API pública do NgRx 18
import {Dictionary, EntityAdapter, EntityState, Update} from '@ngrx/entity';
import {MemoizedSelector} from '@ngrx/store/src/selector';

export const initial = {entities: [], ids: []};

export interface ActionsExample<T> {
  add: ActionCreator<string, (props: { entity: T }) => { entity: T } & { type: string }>;
  adds: ActionCreator<string, (props: { entities: T[] }) => { entities: T[] } & { type: string }>;
  upsert: ActionCreator<string, (props: { entity: T }) => { entity: T } & { type: string }>;
  upserts: ActionCreator<string, (props: { entities: T[] }) => { entities: T[] } & { type: string }>;
  update: ActionCreator<string, (props: { entity: Update<T> }) => { entity: Update<T> } & { type: string }>;
  updates: ActionCreator<string, (props: { entities: Update<T>[] }) => { entities: Update<T>[] } & { type: string }>;
  remove: ActionCreator<string, (props: { id: string }) => { id: string } & { type: string }>;
  removes: ActionCreator<string, (props: { ids: string[] }) => { ids: string[] } & { type: string }>;
  load: ActionCreator<string, (props: { entities: T[] }) => { entities: T[] } & { type: string }>;
  clear: ActionCreator<string, () => object & { type: string }>;
}

export const entityReducer = <T, ES extends EntityState<T>>
(initialState: ES, entityActions: ActionsExample<T>, adapter: EntityAdapter<T>, extra = {}, extraClear = {}): ActionReducer<ES> =>
  createReducer(
    initialState,
    on(entityActions.add, (state, {entity}) => ({...adapter.addOne(entity, state), ...extra})),
    on(entityActions.adds, (state, {entities}) => ({...adapter.addMany(entities, state), ...extra})),
    on(entityActions.upsert, (state, {entity}) => ({...adapter.upsertOne(entity, state), ...extra})),
    on(entityActions.upserts, (state, {entities}) => ({...adapter.upsertMany(entities, state), ...extra})),
    on(entityActions.update, (state, {entity}) => ({...adapter.updateOne(entity, state), ...extra})),
    on(entityActions.updates, (state, {entities}) => ({...adapter.updateMany(entities, state), ...extra})),
    on(entityActions.remove, (state, {id}) => ({...adapter.removeOne(id, state), ...extra})),
    on(entityActions.removes, (state, {ids}) => ({...adapter.removeMany(ids, state), ...extra})),
    on(entityActions.load, (state, {entities}) => ({...adapter.setAll(entities, state)})),
    on(entityActions.clear, state => ({...adapter.removeAll(state), ...extraClear}))
  );

export interface Selectors<T, ES extends EntityState<T>> {
  selectEntityFeature: MemoizedSelector<object, ES, DefaultProjectorFn<ES>>;
  entitySelectAll: MemoizedSelector<object, T[], DefaultProjectorFn<T[]>>;
  entitySelectEntities: MemoizedSelector<object, Dictionary<T>, DefaultProjectorFn<Dictionary<T>>>;
  entitySelectById: (key: string) => MemoizedSelector<object, NonNullable<T> | undefined, DefaultProjectorFn<NonNullable<T> | undefined>>;
}

interface EntitySelectors<T, V> {
  selectEntities: (state: V) => Dictionary<T>;
  selectAll: (state: V) => T[];
}

export const selectors = <T, ES extends EntityState<T>>(featKey: string, {selectEntities, selectAll}: EntitySelectors<T, ES>)
  : Selectors<T, ES> => {
  const selectEntityFeature = createFeatureSelector<ES>(featKey);
  const entitySelectAll = createSelector(selectEntityFeature, selectAll);
  const entitySelectEntities = createSelector(selectEntityFeature, selectEntities);
  const entitySelectById = (key: string) => createSelector(entitySelectEntities, list => list[key] ?? undefined);
  return {entitySelectAll, entitySelectById, entitySelectEntities, selectEntityFeature};
};

export const actions = <T>(entityName: string): ActionsExample<T> => {
  const load = createAction(`[${entityName}/API] Load ${entityName}`, props<{ entities: T[] }>());
  const add = createAction(`[${entityName}/API] Add ${entityName}`, props<{ entity: T }>());
  const adds = createAction(`[${entityName}/API] Add ${entityName}s`, props<{ entities: T[] }>());
  const upsert = createAction(`[${entityName}/API] Upsert ${entityName}`, props<{ entity: T }>());
  const upserts = createAction(`[${entityName}/API] Upsert ${entityName}s`, props<{ entities: T[] }>());
  const update = createAction(`[${entityName}/API] Update ${entityName}`, props<{ entity: Update<T> }>());
  const updates = createAction(`[${entityName}/API] Update ${entityName}s`, props<{ entities: Update<T>[] }>());
  const remove = createAction(`[${entityName}/API] Delete ${entityName}`, props<{ id: string }>());
  const removes = createAction(`[${entityName}/API] Delete ${entityName}s`, props<{ ids: string[] }>());
  const clear = createAction(`[${entityName}/API] Clear ${entityName}`);
  return {add, adds, upsert, upserts, update, updates, remove, removes, load, clear};
};
